/**
 * Green Bubble - product page: columns that follow the scroll
 * ---------------------------------------------------------------------------
 * The product page has two blocks with two columns side by side:
 *
 *   1. product photo |  order block (size, pots, total price, USPs)
 *   2. description   |  specifications
 *
 * In both, one column is much shorter than the other, which leaves an empty
 * gap. This layer pins the shortest column as soon as you scroll past the long
 * column. Which column that is gets measured, not assumed - in block 1 it is
 * usually the photo, in block 2 the specifications, but that can flip per
 * plant.
 *
 * When the short column does not fit in the viewport, the offset is made
 * negative. The effect: the column simply scrolls along until you see its
 * bottom and only then sticks. All content stays reachable, also on a short
 * screen.
 *
 * Everything is recalculated on resize, zoom, and on every content change of
 * the columns (the pot picker grows, for example, when the colour row
 * appears).
 *
 * Deliberately kept apart from assets/app.js - that is a build artefact from
 * the agency's src repo and is overwritten by every new build.
 */
(function () {
  "use strict";

  var BREAKPOINT = 992; // below this width the columns stack
  var MARGE = 16; // breathing room above and below the pinned column
  var MIN_VERSCHIL = 100; // below this height difference pinning is pointless

  var blokken = [];
  var timer = null;
  var ro = null;

  function zichtbaar(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }

  // Height of a fixed header, if any, so the column does not slide underneath it.
  function headerOffset() {
    var hoogste = 0;
    var kandidaten = document.querySelectorAll('header, .header, [class*="header"]');

    for (var i = 0; i < kandidaten.length; i++) {
      var el = kandidaten[i];

      if (!zichtbaar(el)) continue;
      var pos = window.getComputedStyle(el).position;

      if (pos !== "fixed" && pos !== "sticky") continue;
      var r = el.getBoundingClientRect();

      if (r.top > 4) continue; // not stuck to the top

      if (r.height > 200) continue; // not an overlay or drawer

      if (r.height > hoogste) hoogste = r.height;
    }

    return Math.round(hoogste);
  }

  // Two columns side by side inside a Bootstrap row. Deliberately matched on
  // shape and not on class name, so a rearrangement does not break this.
  function vindBlokken() {
    var rijen = document.querySelectorAll("main .container > .row, main .container-fluid > .row");
    var gevonden = [];

    for (var i = 0; i < rijen.length; i++) {
      var rij = rijen[i];
      var kols = [];

      for (var c = 0; c < rij.children.length; c++) {
        var kind = rij.children[c];

        // The product hero grid has the title as a third item, placed above
        // the order block from lg up. Without skipping it the row has three
        // children and the photo is never pinned.
        if (kind.nodeType !== 1 || kind.classList.contains("product-hero__title")) continue;
        kols.push(kind);
      }

      if (kols.length !== 2) continue;

      if (!zichtbaar(kols[0]) || !zichtbaar(kols[1])) continue;
      gevonden.push({ rij: rij, kols: kols, actief: null });
    }

    return gevonden;
  }

  function naastElkaar(blok) {
    var a = blok.kols[0].getBoundingClientRect();
    var b = blok.kols[1].getBoundingClientRect();

    // When they are stacked, they do not overlap horizontally.
    return a.right <= b.left + 1 || b.right <= a.left + 1;
  }

  function losmaken(blok) {
    if (!blok.actief) return;
    blok.actief.classList.remove("gb-sticky-col");
    blok.actief.style.removeProperty("--gb-sticky-top");
    blok.actief = null;
  }

  // The columns are flex items that stretch to the height of the tallest by
  // default. Measured like that they are equally tall by definition and there
  // is nothing to compare. So briefly let both shrink to their own content,
  // measure, and put them back right away. That happens within one frame, so
  // nothing of it is visible.
  function meetInhoudshoogtes(blok) {
    var k0 = blok.kols[0];
    var k1 = blok.kols[1];
    var oud0 = k0.style.alignSelf;
    var oud1 = k1.style.alignSelf;

    k0.style.alignSelf = "flex-start";
    k1.style.alignSelf = "flex-start";

    var h0 = k0.getBoundingClientRect().height;
    var h1 = k1.getBoundingClientRect().height;

    k0.style.alignSelf = oud0;
    k1.style.alignSelf = oud1;

    return [h0, h1];
  }

  function verwerk(blok) {
    // Release first, otherwise you measure the pinned height back.
    losmaken(blok);

    if (window.innerWidth < BREAKPOINT || !naastElkaar(blok)) return;

    // Extra safety: when the columns overlap horizontally by even one pixel,
    // they are really stacked, and pinning would put one column on top of the
    // other. That is exactly the kind of broken rendering you do not want, so
    // rather do nothing then.
    var ra = blok.kols[0].getBoundingClientRect();
    var rb = blok.kols[1].getBoundingClientRect();

    if (ra.right > rb.left + 1 && rb.right > ra.left + 1) return;

    var hoogtes = meetInhoudshoogtes(blok);
    var h0 = hoogtes[0];
    var h1 = hoogtes[1];

    if (Math.abs(h0 - h1) < MIN_VERSCHIL) return;

    var kort = h0 < h1 ? blok.kols[0] : blok.kols[1];
    var kortH = Math.min(h0, h1);

    var boven = headerOffset() + MARGE;
    var ruimte = window.innerHeight - kortH - MARGE;

    // Does the column not fit in the viewport? Then a negative offset: it
    // scrolls along until its bottom is visible and sticks after that.
    var top = Math.min(boven, ruimte);

    kort.style.setProperty("--gb-sticky-top", Math.round(top) + "px");
    kort.classList.add("gb-sticky-col");
    blok.actief = kort;
  }

  function observeer() {
    if (!ro) return;

    for (var i = 0; i < blokken.length; i++) {
      ro.observe(blokken[i].kols[0]);
      ro.observe(blokken[i].kols[1]);
    }
  }

  function verwerkAlles() {
    // The FAQ moves with the screen width, so that has to happen before
    // measuring: it changes the column heights.
    zetFaqOpPlek();

    // On mobile the FAQ sits as a third column in the row and vindBlokken()
    // skips that row. Searching again on every recalculation keeps both
    // situations right, also when you drag the window across the breakpoint.
    blokken = vindBlokken();
    observeer();

    for (var i = 0; i < blokken.length; i++) verwerk(blokken[i]);
  }

  function plan() {
    window.clearTimeout(timer);
    timer = window.setTimeout(verwerkAlles, 120);
  }

  // ---------------------------------------------------------------------------
  // Put the FAQ block in its place
  // ---------------------------------------------------------------------------
  // Liquid renders the block in layout/theme.liquid (so it is simply in the
  // HTML source, also for search engines) and it is moved here. Reason: the
  // product section is a 90 KB file that the API can only rewrite as a whole;
  // that risk is not needed for a block that lands just as well here.
  //
  // WHERE it lands depends on the screen width. On desktop the description and
  // specifications sit side by side and the FAQ belongs under the
  // specifications: there it fills up the shortest column. On mobile those
  // columns stack, so the FAQ ended up between the specifications and the
  // description - you had to scroll past eight collapsed questions before you
  // read what you were buying. On mobile it therefore goes after the
  // description.
  var faqBlok = null;

  function plaatsFaq() {
    var bron = document.getElementById("gb-product-faq-source");

    if (bron) {
      faqBlok = bron.querySelector(".gb-product-faq");

      if (bron.parentNode) bron.parentNode.removeChild(bron);
    }

    zetFaqOpPlek();
  }

  function zetFaqOpPlek() {
    if (!faqBlok) return;

    var specs = document.querySelector(".specifications-accordion");
    var kolom = specs && specs.parentNode;
    var rij = kolom && kolom.parentNode;

    if (!kolom || !rij || !rij.classList || !rij.classList.contains("row")) return;

    if (window.innerWidth < BREAKPOINT) {
      // As the last flex item in the row. order-3 is needed because the two
      // columns already carry order-1 and order-2 on mobile: without the class
      // the FAQ, with order 0, would end up at the top instead.
      if (faqBlok.parentNode !== rij) {
        faqBlok.classList.add("col-12", "order-3", "gb-faq-mobiel");
        rij.appendChild(faqBlok);
      }

      return;
    }

    if (faqBlok.previousElementSibling !== specs) {
      faqBlok.classList.remove("col-12", "order-3", "gb-faq-mobiel");
      kolom.insertBefore(faqBlok, specs.nextSibling);
    }
  }

  // The plant food block belongs in the order form, right above the total
  // price: that is where the customer is already choosing, and the price counts
  // along immediately. Like the FAQ, Liquid renders it and it is moved here.
  // Its checkbox carries the hooks of the existing add-ons, so app.js picks it
  // up by itself.
  function plaatsVoeding() {
    var bron = document.getElementById("gb-product-voeding-source");

    if (!bron) return;

    var blok = bron.querySelector(".gb-voeding");
    var totaal = document.querySelector(".product-price-summary");

    if (!blok || !totaal || !totaal.parentNode) {
      if (bron.parentNode) bron.parentNode.removeChild(bron);

      return;
    }

    // The greeting card sits directly above the total price. The advice belongs
    // to the plant and therefore comes before the card: advice, card, total
    // price.
    var kaartje = totaal.previousElementSibling;
    var anker = kaartje && kaartje.classList.contains("gb-kaartje") ? kaartje : totaal;
    anker.parentNode.insertBefore(blok, anker);
    bron.parentNode.removeChild(bron);
  }

  // ---------------------------------------------------------------------------
  // Fixed bar at the bottom: carry the total price along
  // ---------------------------------------------------------------------------
  // The bar only showed the price of the plant itself. Choose a decorative pot,
  // have it potted or add plant food, and it did not follow - while that bar is
  // exactly the last thing someone sees before clicking. app.js does update the
  // "Total price" block, so that is the source: what it shows, shows here too.
  //
  // Only the digits are copied over, not the markup: the bar keeps its own
  // classes, so nothing changes in the styling and app.js does not come across
  // a second copy of its own hooks.
  // The two blocks write the whole amount slightly differently. In the total
  // block the comma sits in its own span ("112" + <span>,</span>), in the bar it
  // is plain text ("112,"). Copying the number blindly made the comma disappear
  // there. Hence: when the target has its own comma element, replace only the
  // number; otherwise the whole text including the comma.
  function zetGetal(van, naar) {
    if (!van || !naar) return;
    var komma = naar.querySelector(".pdp-price-comma");

    if (!komma) {
      naar.textContent = van.textContent;

      return;
    }

    var getal =
      van.firstChild && van.firstChild.nodeType === 3
        ? van.firstChild.nodeValue
        : van.textContent.replace(/[^0-9]/g, "");

    var eerste = naar.firstChild;

    if (eerste && eerste.nodeType === 3) {
      eerste.nodeValue = getal;
    } else {
      naar.insertBefore(document.createTextNode(getal), komma);
    }
  }

  function kopieerBedrag(van, naar) {
    if (!van || !naar) return;

    var vk = van.children,
      nk = naar.children;

    if (vk.length >= 2 && nk.length >= 2) {
      zetGetal(vk[0], nk[0]);
      nk[1].textContent = vk[1].textContent;
    } else {
      naar.textContent = van.textContent;
    }
  }

  // The price after "Oppotten" (potting) depends on the chosen pot size, so
  // app.js rewrites that text itself. It goes through window.GB_PRICE_FORMAT
  // and so places the currency sign right straight away: no sign before kr and
  // zl in the wrong place, and lately also the euro sign: €12,95 in Dutch,
  // 12,95 € in German.
  //
  // Here was a cleaner that stripped every euro sign from
  // .js-addon-price-display, because the theme deliberately showed the euro
  // without a sign. That assumption no longer holds: it now removed exactly the
  // sign that Liquid and app.js put there on purpose. Left empty instead of
  // removed, so the call below keeps working and the diff shows what changed.
  function haalEuroWeg() {}

  function syncBalk() {
    haalEuroWeg();

    var totaal = document.querySelector(".product-price-summary__total");
    var balk = document.querySelector(".product-cta .js-sticky-price");

    if (!totaal || !balk) return;

    kopieerBedrag(
      totaal.querySelector(".js-price-summary-total"),
      balk.querySelector(".js-sticky-price-value"),
    );

    var vanStreep = totaal.querySelector(".js-price-summary-compare");
    var naarStreep = balk.querySelector(".product-cta--sale");

    if (naarStreep) {
      var toon = !!(
        vanStreep &&
        vanStreep.textContent.replace(/\s/g, "") &&
        window.getComputedStyle(vanStreep).display !== "none"
      );

      if (toon) kopieerBedrag(vanStreep, naarStreep);
      naarStreep.style.display = toon ? "" : "none";
    }
  }

  function volgTotaalprijs() {
    var totaal = document.querySelector(".product-price-summary__total");

    if (!totaal) return;

    syncBalk();

    if (window.MutationObserver) {
      // app.js rewrites the total block on every change: pot, potting, plant
      // food, quantity, size. Watching along is more reliable than trying to
      // catch all those events separately.
      new MutationObserver(function () {
        syncBalk();
      }).observe(totaal, { childList: true, subtree: true, characterData: true });
    }

    // When the size changes, the theme sometimes rebuilds the bar and our
    // digits are gone. Check again a few times after such a change.
    document.addEventListener(
      "change",
      function () {
        window.setTimeout(syncBalk, 60);
        window.setTimeout(syncBalk, 400);
        window.setTimeout(syncBalk, 1200);
      },
      true,
    );
  }

  function start() {
    plaatsFaq();
    plaatsVoeding();
    volgTotaalprijs();

    // Content that arrives later: images, the pot picker unfolding its colour
    // row, lazily loaded blocks. The observer is created here and pointed at
    // the found columns in verwerkAlles(), because that set changes as soon as
    // the FAQ moves across the breakpoint.
    if (window.ResizeObserver) ro = new ResizeObserver(plan);

    verwerkAlles();

    window.addEventListener("resize", plan);
    window.addEventListener("orientationchange", plan);

    // As soon as you drop below the breakpoint width everything has to be
    // released at once, not only after the delay of plan(). Otherwise rotating
    // a phone briefly shows a pinned column lying over the other one.
    if (window.matchMedia) {
      var mq = window.matchMedia("(min-width: " + BREAKPOINT + "px)");

      var opBreedte = function () {
        if (!mq.matches) {
          for (var i = 0; i < blokken.length; i++) losmaken(blokken[i]);
        }

        plan();
      };

      if (mq.addEventListener) mq.addEventListener("change", opBreedte);
      else if (mq.addListener) mq.addListener(opBreedte);
    }

    window.addEventListener("load", plan);
    window.setTimeout(verwerkAlles, 1200);
    window.setTimeout(verwerkAlles, 4000);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
